import React, { useState } from 'react';

import {
    GoogleMap,
    InfoWindow,
    Marker,
    useJsApiLoader,
} from '@react-google-maps/api';

import { Text, WDSButton } from 'wanderu-components';
import { appSettings } from 'wanderu-config';

import LocationIcon from './LocationIcon.svg';
import { RadioButton } from './RadioButton';
import events from './events';

import styles from './StationMap.scss';

import type { MarkerProps } from '@react-google-maps/api';

const Directions = {
    arrive: 'arrive',
    depart: 'depart',
} as const;

type Position = MarkerProps['position'];

type MapMode = keyof typeof Directions;

const containerStyle = {
    height: '400px',
    width: '100%',
};

interface SearchParams {
    departDate: string;
    departTripId?: string;
    destinationPlaceId?: string;
    destinationText: string;
    direction_displayed: MapMode;
    originPlaceId?: string;
    originText: string;
    returnDate?: string;
}

interface Station {
    name: string;
    id: string;
    position: Position
}

interface Trip {
    arriveId: string;
    arriveLatLong: Position;
    arriveName: string;
    carrierCode: string;
    departId: string;
    departLatLong: Position;
    departName: string;
    duration: number;
    tripId: string;
}

interface StationMapProps {
    arriveLocation: Position;
    departLocation: Position;
    isArrivalStationsFiltered: boolean;
    isDepartureStationsFiltered: boolean;
    resetArrivalStations: () => void;
    resetDepartureStations: () => void;
    searchParams: SearchParams;
    setArrivalStationOnlyActive: (stationId: string) => void;
    setDepartureStationOnlyActive: (stationId: string) => void;
    trips: Trip[];
}

function StationMap({
    arriveLocation,
    departLocation,
    isArrivalStationsFiltered,
    isDepartureStationsFiltered,
    resetArrivalStations,
    resetDepartureStations,
    searchParams,
    setArrivalStationOnlyActive,
    setDepartureStationOnlyActive,
    trips,
}: StationMapProps) {
    const [mapMode, setMapMode] = useState<MapMode>(Directions.depart);
    const [station, setStation] = useState<Station>();

    const { isLoaded } = useJsApiLoader({
        googleMapsApiKey: appSettings.config.google.apiKey,
        id: 'google-map-script',
    });

    const options = [
        {
            id: Directions.depart,
            text: 'Departure Locations',
        },
        {
            id: Directions.arrive,
            text: 'Arrival Locations',
        },
    ];

    const isDepart = mapMode === Directions.depart;

    const center = isDepart
        ? departLocation
        : arriveLocation;

    const properties = {
        destination: {
            name: searchParams.destinationText,
            place_id: searchParams.destinationPlaceId,
        },
        direction: isDepart
            ? 'origin'
            : 'destination',
        is_one_way: !searchParams.returnDate,
        origin: {
            name: searchParams.originText,
            place_id: searchParams.originPlaceId,
        },
        outbound_date: searchParams.departDate,
        return_date: searchParams.returnDate,
    };

    const onDirectionClick = (id: MapMode) => {
        events.clickDirectionEvent({
            new_direction: id === 'depart'
                ? 'origin'
                : 'destination',
            ...properties,
        });

        setMapMode(id);
        setStation(undefined);
    };

    const onPinClick = (trip: Trip) => {
        const position = trip[`${mapMode}LatLong`];
        const id = trip[`${mapMode}Id`];
        const name = trip[`${mapMode}Name`];

        events.clickPinEvent({
            station: {
                id,
                name,
            },
            ...properties,
        });

        setStation({
            id: trip[`${mapMode}Id`],
            name: trip[`${mapMode}Name`],
            position,
        });
    };

    const onLinkClick = ({ id, name }: Station) => {
        events.clickFilterEvent({
            station: {
                id,
                name,
            },
            ...properties,
        });

        return isDepart
            ? setDepartureStationOnlyActive(id)
            : setArrivalStationOnlyActive(id);
    };

    const onResetClick = () => {
        events.clickResetEvent(properties);

        resetDepartureStations();
        resetArrivalStations();
    };

    const linkText = isDepart
        ? 'View trips leaving from this station'
        : 'View trips arriving to this station';

    return isLoaded
        ? (
            <div className={styles.container}>
                <div className={styles.controls}>
                    {options.map(({ id, text }) => (
                        <RadioButton
                            checked={id === mapMode}
                            id={id}
                            key={id}
                            onChange={() => onDirectionClick(id)}
                            text={text}
                        />
                    ))}

                    {isArrivalStationsFiltered || isDepartureStationsFiltered
                        ? <WDSButton className={styles.reset} onClick={onResetClick} variant="unstyled">Show All Stations</WDSButton>
                        : ''}
                </div>
                <GoogleMap
                    center={center}
                    mapContainerStyle={containerStyle}
                    options={{
                        disableDefaultUI: true,
                        scaleControl: true,
                        zoomControl: true,
                    }}
                    zoom={11}
                >
                    <Marker
                        clickable={false}
                        icon={LocationIcon}
                        position={center}
                    />
                    {trips?.map((trip) => {
                        const position = trip[`${mapMode}LatLong`];

                        return position && <Marker
                            key={trip.tripId}
                            onClick={() => onPinClick(trip)}
                            position={position}
                        />;
                    })}

                    {station && (
                        <InfoWindow
                            onCloseClick={() => setStation(undefined)}
                            options={{ pixelOffset: new window.google.maps.Size(0, -40) }}
                            position={station?.position}
                        >
                            <div>
                                <Text component="h1">{station?.name}</Text>
                                <WDSButton onClick={() => onLinkClick(station)} variant="unstyled">
                                    {linkText}
                                </WDSButton>
                            </div>
                        </InfoWindow>
                    )}
                </GoogleMap>
            </div>
        )
        : <></>;
}

export default React.memo(StationMap);
